'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Search, BookOpen, Tag, ArrowRight } from 'lucide-react';
import CTASection from '@/components/sections/CTASection';
import JsonLd from '@/components/common/JsonLd';

import { glossaryTerms } from './glossaryData';


const GlossaryContent = () => {
    const [searchQuery, setSearchQuery] = useState("");

    const filteredTerms = glossaryTerms.filter(t => 
        t.term.toLowerCase().includes(searchQuery.toLowerCase()) || 
        t.definition.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <main className="w-full bg-slate-50 pt-32 pb-0 min-h-screen">
            <JsonLd 
                type="FAQPage" 
                data={glossaryTerms.map(t => ({
                    question: t.question,
                    answer: t.answer
                }))} 
            />
            {/* Header Section */}
            <section className="w-full max-w-[1000px] mx-auto px-6 mb-16 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm mb-6">
                    <BookOpen size={16} />
                    Label Manufacturing Dictionary
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#1D2B4F] mb-6 leading-tight">
                    Industry Glossary
                </h1>
                <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
                    Master the technical terminology of industrial label manufacturing. Understand the materials, compliance standards, and technologies that protect your brand.
                </p>

                {/* Search Bar */}
                <div className="mt-10 relative max-w-[600px] mx-auto">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search for terms (e.g. Flexographic, FSC, Adhesives)..."
                        className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent text-slate-700 text-lg transition-all"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </section>

            {/* Glossary List */}
            <section className="w-full max-w-[1000px] mx-auto px-6 pb-24">
                {filteredTerms.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-3xl border border-slate-200">
                        <p className="text-xl text-slate-500 font-medium">No terminology found matching "{searchQuery}"</p>
                        <button onClick={() => setSearchQuery('')} className="mt-4 text-blue-600 font-bold hover:underline">Clear Search</button>
                    </div>
                ) : (
                    <div className="space-y-12">
                        {filteredTerms.map((item, idx) => (
                            <motion.article 
                                key={item.id}
                                id={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                className="bg-white rounded-3xl p-8 md:p-10 border border-slate-200 shadow-sm scroll-mt-32"
                            >
                                <div className="flex items-start gap-4 mb-6 border-b border-slate-100 pb-6">
                                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0 mt-1">
                                        <Tag size={24} />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl md:text-3xl font-bold text-[#1D2B4F] mb-3">{item.term}</h2>
                                        <p className="text-lg text-slate-600 leading-relaxed font-medium">{item.definition}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                    <div>
                                        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-3">Industry Relevance</h3>
                                        <p className="text-slate-700 leading-relaxed">{item.relevance}</p>
                                    </div>
                                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                                        <h3 className="text-lg font-bold text-[#1D2B4F] mb-2">{item.question}</h3>
                                        <p className="text-slate-600 leading-relaxed text-sm">{item.answer}</p>
                                    </div>
                                </div>

                                {item.links && item.links.length > 0 && (
                                    <div className="pt-6 border-t border-slate-100">
                                        {item.links.map((link, lIdx) => (
                                            <Link key={lIdx} href={link.url} className="inline-flex items-center gap-2 text-blue-600 font-bold hover:text-blue-800 transition-colors group">
                                                {link.text}
                                                <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </motion.article>
                        ))}
                    </div>
                )}
            </section>

            <CTASection
                title="Need Expert Guidance on Your Label Specs?"
                description="Our engineering team is ready to translate complex technical requirements into a flawless manufacturing strategy for your brand."
                buttonText="Talk to Experts"
                href="/contact"
            />
        </main>
    );
};

export default GlossaryContent;
